// Case Events Hooks
// BP-10-04: Attorney Frontend

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { caseEventsApi } from '@/features/attorney/api/attorneyApi';
import type { CaseEvent, CaseEventFormData } from '@/features/attorney/types/attorney';
import { casesKeys } from './useCases';

// Query Keys
export const caseEventsKeys = {
  all: ['case-events'] as const,
  lists: () => [...caseEventsKeys.all, 'list'] as const,
  list: (caseId: string) => [...caseEventsKeys.lists(), caseId] as const,
  details: () => [...caseEventsKeys.all, 'detail'] as const,
  detail: (eventId: string) => [...caseEventsKeys.details(), eventId] as const,
};

/**
 * Hook to fetch events for a case
 */
export function useCaseEvents(caseId: string | undefined, page = 1, pageSize = 50) {
  return useQuery({
    queryKey: caseEventsKeys.list(caseId!),
    queryFn: () => caseEventsApi.getEvents(caseId!, page, pageSize),
    enabled: !!caseId,
  });
}

/**
 * Hook to fetch a single event
 */
export function useCaseEvent(eventId: string | undefined) {
  return useQuery({
    queryKey: caseEventsKeys.detail(eventId!),
    queryFn: () => caseEventsApi.getEvent(eventId!),
    enabled: !!eventId,
  });
}

/**
 * Hook to create a new event
 */
export function useCreateEvent(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CaseEventFormData) => caseEventsApi.createEvent(caseId, data),
    onSuccess: (newEvent) => {
      // Add to events list
      queryClient.setQueryData(
        caseEventsKeys.list(caseId),
        (old: { events: CaseEvent[]; total: number } | undefined) => {
          if (!old) return { events: [newEvent], total: 1 };
          return {
            events: [newEvent, ...old.events],
            total: old.total + 1,
          };
        }
      );
      // Invalidate case to update event count
      queryClient.invalidateQueries({ queryKey: casesKeys.detail(caseId) });
      // Invalidate dashboard for recent activity
      queryClient.invalidateQueries({ queryKey: casesKeys.dashboard() });
    },
  });
}

/**
 * Hook to update an event
 */
export function useUpdateEvent(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: Partial<CaseEventFormData> }) =>
      caseEventsApi.updateEvent(eventId, data),
    onSuccess: (updatedEvent) => {
      // Update event in detail
      queryClient.setQueryData(caseEventsKeys.detail(updatedEvent.id), updatedEvent);
      // Update event in list
      queryClient.setQueryData(
        caseEventsKeys.list(caseId),
        (old: { events: CaseEvent[]; total: number } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            events: old.events.map((e) => (e.id === updatedEvent.id ? updatedEvent : e)),
          };
        }
      );
    },
  });
}

/**
 * Hook to delete an event
 */
export function useDeleteEvent(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => caseEventsApi.deleteEvent(eventId),
    onSuccess: (_, eventId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: caseEventsKeys.detail(eventId) });
      // Update list
      queryClient.setQueryData(
        caseEventsKeys.list(caseId),
        (old: { events: CaseEvent[]; total: number } | undefined) => {
          if (!old) return old;
          return {
            events: old.events.filter((e) => e.id !== eventId),
            total: old.total - 1,
          };
        }
      );
      // Invalidate case
      queryClient.invalidateQueries({ queryKey: casesKeys.detail(caseId) });
    },
  });
}

/**
 * Hook to mark event as completed
 */
export function useCompleteEvent(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => caseEventsApi.completeEvent(eventId),
    onSuccess: (updatedEvent) => {
      // Update in caches
      queryClient.setQueryData(caseEventsKeys.detail(updatedEvent.id), updatedEvent);
      queryClient.setQueryData(
        caseEventsKeys.list(caseId),
        (old: { events: CaseEvent[]; total: number } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            events: old.events.map((e) => (e.id === updatedEvent.id ? updatedEvent : e)),
          };
        }
      );
    },
  });
}

/**
 * Hook to upload attachment to event
 */
export function useUploadEventAttachment(caseId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, file }: { eventId: string; file: File }) =>
      caseEventsApi.uploadAttachment(eventId, file),
    onSuccess: (updatedEvent) => {
      queryClient.setQueryData(caseEventsKeys.detail(updatedEvent.id), updatedEvent);
      queryClient.setQueryData(
        caseEventsKeys.list(caseId),
        (old: { events: CaseEvent[]; total: number } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            events: old.events.map((e) => (e.id === updatedEvent.id ? updatedEvent : e)),
          };
        }
      );
    },
  });
}

/**
 * Hook to get upcoming deadlines across all cases
 */
export function useUpcomingDeadlines() {
  // This uses the dashboard stats which includes upcoming deadlines
  return useQuery({
    queryKey: [...caseEventsKeys.all, 'upcoming'],
    queryFn: async () => {
      // We can get this from the dashboard stats
      const response = await fetch('/api/attorney/events/upcoming');
      return response.json();
    },
  });
}


